import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  MyLocation as LocationIcon,
} from '@mui/icons-material';
import type { StarSystem, GalaxyMapData } from '../../types/gameState';

interface GalaxyMapProps {
  mapData: GalaxyMapData | null;
  currentSystemId: string | null;
  onSystemSelect?: (system: StarSystem) => void;
  isLoading?: boolean;
}

interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const DEFAULT_VIEWBOX: ViewBox = {
  x: -50,
  y: -50,
  width: 100,
  height: 100,
};

const STAR_COLORS: Record<string, string> = {
  O: '#9bb0ff',
  B: '#aabfff',
  A: '#cad7ff',
  F: '#f8f7ff',
  G: '#fff4ea',
  K: '#ffd2a1',
  M: '#ffcc6f',
  default: '#ffffff',
};

function getStarColor(starClass: string | null): string {
  if (!starClass) return STAR_COLORS.default;
  const firstLetter = starClass.charAt(0).toUpperCase();
  return STAR_COLORS[firstLetter] || STAR_COLORS.default;
}

function getJumpLaneColor(laneType: string, hazardLevel: number): string {
  if (hazardLevel > 5) return '#ff4444';
  if (laneType === 'hazardous') return '#ff8800';
  if (laneType === 'restricted') return '#ff00ff';
  if (laneType === 'wormhole') return '#00ffff';
  return '#4488ff';
}

export function GalaxyMap({
  mapData,
  currentSystemId,
  onSystemSelect,
  isLoading,
}: GalaxyMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewBox, setViewBox] = useState<ViewBox>(DEFAULT_VIEWBOX);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOrigin, setDragOrigin] = useState({ x: 0, y: 0 }); // Original mouse down position
  const [dragDistance, setDragDistance] = useState(0);
  const [hoveredSystem, setHoveredSystem] = useState<StarSystem | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<StarSystem | null>(null);

  // Touch state for mobile gestures
  const [lastTouches, setLastTouches] = useState<React.TouchList | null>(null);
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);

  // Scale factor for map spacing (5x = systems five times as far apart)
  const MAP_SCALE = 5;

  // Calculate bounds from map data
  const bounds = useMemo(() => {
    if (!mapData?.systems?.length) return null;

    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;

    mapData.systems.forEach((sys) => {
      const scaledX = sys.map_x * MAP_SCALE;
      const scaledY = sys.map_y * MAP_SCALE;
      if (scaledX < minX) minX = scaledX;
      if (scaledX > maxX) maxX = scaledX;
      if (scaledY < minY) minY = scaledY;
      if (scaledY > maxY) maxY = scaledY;
    });

    const padding = 20;
    return {
      minX: minX - padding,
      maxX: maxX + padding,
      minY: minY - padding,
      maxY: maxY + padding,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2,
    };
  }, [mapData]);

  // Center on current system
  const centerOnSystem = useCallback(
    (systemId: string | null) => {
      if (!systemId || !mapData?.systems) return;

      const system = mapData.systems.find((s) => s.id === systemId);
      if (!system) return;

      setViewBox((prev) => ({
        ...prev,
        x: system.map_x * MAP_SCALE - prev.width / 2,
        y: system.map_y * MAP_SCALE - prev.height / 2,
      }));
    },
    [mapData]
  );

  // Initialize to zoomed-in view when map data loads (50% zoom = 2x magnification)
  useEffect(() => {
    if (bounds && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth || 400;
      const containerHeight = containerRef.current.clientHeight || 200;
      const aspectRatio = containerWidth / containerHeight;

      // Start at 50% of full view (2x zoom in)
      let width = bounds.width * 0.5;
      let height = bounds.height * 0.5;

      if (width / height > aspectRatio) {
        height = width / aspectRatio;
      } else {
        width = height * aspectRatio;
      }

      // Center on the middle of the map
      const centerX = (bounds.minX + bounds.maxX) / 2;
      const centerY = (bounds.minY + bounds.maxY) / 2;

      setViewBox({
        x: centerX - width / 2,
        y: centerY - height / 2,
        width,
        height,
      });
    }
  }, [bounds]);

  // Center on current system when it changes (but not on initial load)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (currentSystemId && hasInitialized.current) {
      centerOnSystem(currentSystemId);
    }
    if (currentSystemId) {
      hasInitialized.current = true;
    }
  }, [currentSystemId, centerOnSystem]);

  // Reset to default zoom (50% zoom = 2x magnification, same as initial load)
  const resetToDefaultZoom = useCallback(() => {
    if (!bounds) return;

    const containerWidth = containerRef.current?.clientWidth || 400;
    const containerHeight = containerRef.current?.clientHeight || 200;
    const aspectRatio = containerWidth / containerHeight;

    // Reset to 50% of full view (2x zoom in, same as initial)
    let width = bounds.width * 0.5;
    let height = bounds.height * 0.5;

    if (width / height > aspectRatio) {
      height = width / aspectRatio;
    } else {
      width = height * aspectRatio;
    }

    // Center on the middle of the map
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    setViewBox({
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    });
  }, [bounds]);

  // Zoom handlers
  const handleZoomIn = () => {
    setViewBox((prev) => ({
      x: prev.x + prev.width * 0.125,
      y: prev.y + prev.height * 0.125,
      width: prev.width * 0.75,
      height: prev.height * 0.75,
    }));
  };

  const handleZoomOut = () => {
    setViewBox((prev) => ({
      x: prev.x - prev.width * 0.25,
      y: prev.y - prev.height * 0.25,
      width: prev.width * 1.5,
      height: prev.height * 1.5,
    }));
  };

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setDragOrigin({ x: e.clientX, y: e.clientY }); // Save original position
    setDragDistance(0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !svgRef.current) return;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const scaleX = viewBox.width / rect.width;
    const scaleY = viewBox.height / rect.height;

    const dx = (e.clientX - dragStart.x) * scaleX;
    const dy = (e.clientY - dragStart.y) * scaleY;

    // Track total distance from original mouse down position
    const totalDx = Math.abs(e.clientX - dragOrigin.x);
    const totalDy = Math.abs(e.clientY - dragOrigin.y);
    setDragDistance(totalDx + totalDy);

    setViewBox((prev) => ({
      ...prev,
      x: prev.x - dx,
      y: prev.y - dy,
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Reset drag distance after mouse up so next click works
    setTimeout(() => setDragDistance(0), 0);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;

    // Get mouse position relative to SVG
    if (!svgRef.current) return;
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * viewBox.width + viewBox.x;
    const mouseY = ((e.clientY - rect.top) / rect.height) * viewBox.height + viewBox.y;

    const newWidth = viewBox.width * zoomFactor;
    const newHeight = viewBox.height * zoomFactor;

    // Zoom towards mouse position
    const newX = mouseX - (mouseX - viewBox.x) * zoomFactor;
    const newY = mouseY - (mouseY - viewBox.y) * zoomFactor;

    setViewBox({
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    });
  };

  // Touch event handlers for mobile pan and pinch-zoom
  const getTouchDistance = (touches: React.TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touches: React.TouchList): { x: number; y: number } => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    const x = (touches[0].clientX + touches[1].clientX) / 2;
    const y = (touches[0].clientY + touches[1].clientY) / 2;
    return { x, y };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setLastTouches(e.touches);

    if (e.touches.length === 2) {
      // Start pinch zoom
      setInitialPinchDistance(getTouchDistance(e.touches));
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      // Start pan
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      setDragOrigin({ x: e.touches[0].clientX, y: e.touches[0].clientY }); // Save original touch position
      setDragDistance(0);
      setInitialPinchDistance(null);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!svgRef.current || !lastTouches) return;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();

    if (e.touches.length === 2 && initialPinchDistance) {
      // Handle pinch zoom
      const currentDistance = getTouchDistance(e.touches);
      const zoomFactor = initialPinchDistance / currentDistance;

      // Get pinch center in SVG coordinates
      const center = getTouchCenter(e.touches);
      const svgX = ((center.x - rect.left) / rect.width) * viewBox.width + viewBox.x;
      const svgY = ((center.y - rect.top) / rect.height) * viewBox.height + viewBox.y;

      const newWidth = viewBox.width * zoomFactor;
      const newHeight = viewBox.height * zoomFactor;

      // Zoom towards pinch center
      const newX = svgX - (svgX - viewBox.x) * zoomFactor;
      const newY = svgY - (svgY - viewBox.y) * zoomFactor;

      setViewBox({
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      });

      setInitialPinchDistance(currentDistance);
    } else if (e.touches.length === 1 && isDragging) {
      // Handle pan
      const scaleX = viewBox.width / rect.width;
      const scaleY = viewBox.height / rect.height;

      const dx = (e.touches[0].clientX - dragStart.x) * scaleX;
      const dy = (e.touches[0].clientY - dragStart.y) * scaleY;

      // Track total distance from original touch position
      const totalDx = Math.abs(e.touches[0].clientX - dragOrigin.x);
      const totalDy = Math.abs(e.touches[0].clientY - dragOrigin.y);
      setDragDistance(totalDx + totalDy);

      setViewBox((prev) => ({
        ...prev,
        x: prev.x - dx,
        y: prev.y - dy,
      }));

      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }

    setLastTouches(e.touches);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();

    if (e.touches.length < 2) {
      setInitialPinchDistance(null);
    }

    if (e.touches.length === 0) {
      setIsDragging(false);
      setLastTouches(null);
      // Reset drag distance after touch end so next tap works
      setTimeout(() => setDragDistance(0), 0);
    } else {
      setLastTouches(e.touches);
    }
  };

  // System click handler
  const handleSystemClick = (e: React.MouseEvent, system: StarSystem) => {
    // Only handle click if we didn't drag (drag distance < 5 pixels for tolerance)
    if (dragDistance < 5) {
      e.stopPropagation();
      setSelectedSystem(system);
      onSystemSelect?.(system);
    }
  };

  if (isLoading) {
    return (
      <Card
        sx={{
          height: '100%',
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <CardContent sx={{ height: '100%', p: 1 }}>
          <Skeleton variant="rectangular" height="100%" />
        </CardContent>
      </Card>
    );
  }

  if (!mapData || !mapData.systems?.length) {
    return (
      <Card
        sx={{
          height: '100%',
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Galaxy map data unavailable
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        backgroundColor: '#0a0a12',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Map Controls */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          backgroundColor: 'rgba(0,0,0,0.7)',
          borderRadius: 1,
          p: 0.5,
        }}
      >
        <Tooltip title="Zoom In" placement="left">
          <IconButton size="small" onClick={handleZoomIn}>
            <ZoomInIcon sx={{ fontSize: 18, color: 'white' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out" placement="left">
          <IconButton size="small" onClick={handleZoomOut}>
            <ZoomOutIcon sx={{ fontSize: 18, color: 'white' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset Zoom" placement="left">
          <IconButton size="small" onClick={resetToDefaultZoom}>
            <CenterIcon sx={{ fontSize: 18, color: 'white' }} />
          </IconButton>
        </Tooltip>
        {currentSystemId && (
          <Tooltip title="Current Location" placement="left">
            <IconButton
              size="small"
              onClick={() => centerOnSystem(currentSystemId)}
            >
              <LocationIcon sx={{ fontSize: 18, color: '#00ff00' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* System Info Tooltip */}
      {(hoveredSystem || selectedSystem) && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.85)',
            borderRadius: 1,
            p: 1,
            minWidth: 150,
            border: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: 'white', fontWeight: 600 }}
          >
            {(hoveredSystem || selectedSystem)?.name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {(hoveredSystem || selectedSystem)?.economy_type && (
              <Chip
                label={(hoveredSystem || selectedSystem)?.economy_type?.replace('_', ' ')}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  backgroundColor: 'rgba(100,100,255,0.3)',
                  color: 'white',
                }}
              />
            )}
            {(hoveredSystem || selectedSystem)?.security_level && (
              <Chip
                label={(hoveredSystem || selectedSystem)?.security_level}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  backgroundColor:
                    (hoveredSystem || selectedSystem)?.security_level === 'high'
                      ? 'rgba(0,255,0,0.3)'
                      : (hoveredSystem || selectedSystem)?.security_level === 'low'
                        ? 'rgba(255,0,0,0.3)'
                        : 'rgba(255,165,0,0.3)',
                  color: 'white',
                }}
              />
            )}
          </Box>
          {(hoveredSystem || selectedSystem)?.id === currentSystemId && (
            <Typography
              variant="caption"
              sx={{ color: '#00ff00', display: 'block', mt: 0.5 }}
            >
              You are here
            </Typography>
          )}
        </Box>
      )}

      {/* SVG Map */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ display: 'block', touchAction: 'none' }}
        >
          {/* Background stars (decorative) */}
          <defs>
            <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="0.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="currentGlow">
              <feGaussianBlur stdDeviation="1" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Jump Lanes */}
          <g className="jump-lanes">
            {mapData.jumpLanes?.map((lane) => {
              const fromSystem = mapData.systems.find(
                (s) => s.id === lane.from_system_id
              );
              const toSystem = mapData.systems.find(
                (s) => s.id === lane.to_system_id
              );
              if (!fromSystem || !toSystem) return null;

              return (
                <line
                  key={lane.id}
                  x1={fromSystem.map_x * MAP_SCALE}
                  y1={fromSystem.map_y * MAP_SCALE}
                  x2={toSystem.map_x * MAP_SCALE}
                  y2={toSystem.map_y * MAP_SCALE}
                  stroke={getJumpLaneColor(lane.jump_type, lane.hazard_level)}
                  strokeWidth={0.3}
                  strokeOpacity={0.5}
                  strokeDasharray={
                    lane.jump_type === 'hazardous' ? '1,1' : undefined
                  }
                />
              );
            })}
          </g>

          {/* Star Systems */}
          <g className="systems">
            {mapData.systems.map((system) => {
              const isCurrent = system.id === currentSystemId;
              const isHovered = system.id === hoveredSystem?.id;
              const isSelected = system.id === selectedSystem?.id;
              const starColor = getStarColor(system.star_class);
              const radius = isCurrent ? 2 : 1.2;

              return (
                <g
                  key={system.id}
                  transform={`translate(${system.map_x * MAP_SCALE}, ${system.map_y * MAP_SCALE})`}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredSystem(system)}
                  onMouseLeave={() => setHoveredSystem(null)}
                  onClick={(e) => handleSystemClick(e, system)}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {/* Current system indicator */}
                  {isCurrent && (
                    <circle
                      r={4}
                      fill="none"
                      stroke="#00ff00"
                      strokeWidth={0.3}
                      filter="url(#currentGlow)"
                    >
                      <animate
                        attributeName="r"
                        values="3;5;3"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="1;0.3;1"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  )}

                  {/* Star */}
                  <circle
                    r={radius}
                    fill={starColor}
                    filter="url(#glow)"
                    opacity={isHovered || isSelected ? 1 : 0.8}
                  />

                  {/* Hover/selected ring */}
                  {(isHovered || isSelected) && !isCurrent && (
                    <circle
                      r={radius + 1}
                      fill="none"
                      stroke="white"
                      strokeWidth={0.2}
                      opacity={0.7}
                    />
                  )}

                  {/* System name (only show when zoomed in enough) */}
                  {viewBox.width < 60 && (
                    <text
                      y={radius + 2}
                      fill="white"
                      fontSize={1.5}
                      textAnchor="middle"
                      opacity={0.7}
                    >
                      {system.name}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </Box>
    </Card>
  );
}
